import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

import { formatHour12 } from './availabilitySummary';
import { hhmmFromMinutes, minutesFromHhmm, talkerDayOfWeek } from './talkerAvailability';
import { canonicalizeTimeZone } from './timeZone';

const YMD = 'YYYY-MM-DD';

/**
 * YYYY-MM-DD for a date in a timezone.
 *
 * @param {Date|string|number} date
 * @param {string} [timeZone]
 * @returns {string}
 */
export const ymdInTimeZone = (date, timeZone = 'Etc/UTC') =>
  moment(date)
    .tz(canonicalizeTimeZone(timeZone) || 'Etc/UTC')
    .format(YMD);

/**
 * Add days to a YYYY-MM-DD string.
 *
 * @param {string} ymd
 * @param {number} days
 * @returns {string}
 */
export const addYmdDays = (ymd, days) =>
  moment
    .tz(ymd, YMD, 'Etc/UTC')
    .add(days, 'days')
    .format(YMD);

/**
 * Inclusive YYYY-MM-DD list from start to end.
 *
 * @param {string} startYmd
 * @param {string} endYmd
 * @returns {string[]}
 */
export const eachYmd = (startYmd, endYmd) => {
  const last = endYmd && endYmd > startYmd ? endYmd : startYmd;
  const out = [];
  let cursor = startYmd;
  while (cursor <= last) {
    out.push(cursor);
    cursor = addYmdDays(cursor, 1);
  }
  return out;
};

/**
 * Instant for YYYY-MM-DD + HH:mm in a timezone. 24:00 is next midnight.
 *
 * @param {string} ymd
 * @param {string} hhmm
 * @param {string} [timeZone]
 * @returns {Date}
 */
export const zonedDateFromParts = (ymd, hhmm, timeZone = 'Etc/UTC') => {
  const zone = canonicalizeTimeZone(timeZone) || 'Etc/UTC';
  if (hhmm === '24:00') {
    return moment
      .tz(`${ymd} 00:00`, `${YMD} HH:mm`, zone)
      .add(1, 'day')
      .toDate();
  }
  return moment.tz(`${ymd} ${hhmm}`, `${YMD} HH:mm`, zone).toDate();
};

/**
 * 30-minute clock options for the one-day editor.
 *
 * @param {boolean} [isEnd]
 * @returns {Array<{ value: string, label: string }>}
 */
export const changeOneDayHourOptions = (isEnd = false) => {
  const start = isEnd ? 30 : 0;
  const stop = isEnd ? 24 * 60 : 23 * 60 + 30;
  const hours = [];
  for (let minutes = start; minutes <= stop; minutes += 30) {
    const value = minutes === 24 * 60 ? '24:00' : hhmmFromMinutes(minutes);
    hours.push({ value, label: formatHour12(value) });
  }
  return hours;
};

const usualForYmd = (ymd, entries = [], timeZone) => {
  const day = talkerDayOfWeek(zonedDateFromParts(ymd, '12:00', timeZone), timeZone);
  return (entries || []).find(entry => entry?.dayOfWeek === day) || null;
};

const exception = (ymd, startHhmm, endHhmm, timeZone, seats) => ({
  start: zonedDateFromParts(ymd, startHhmm, timeZone),
  end: zonedDateFromParts(ymd, endHhmm, timeZone),
  seats,
});

const asDate = value => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const EXCEPTION_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Next interval start that is still in the future. Sharetribe rejects
 * exception starts in the past, and starts must land on the plan interval.
 *
 * @param {Date|string|number} date
 * @param {Date|number} [now]
 * @returns {Date}
 */
export const alignExceptionStart = (date, now = new Date()) => {
  const min = now instanceof Date ? now.getTime() + 1000 : Number(now) + 1000;
  const start = date instanceof Date ? date.getTime() : new Date(date).getTime();
  const later = Number.isFinite(start) && start > min ? start : min;
  return new Date(Math.ceil(later / EXCEPTION_INTERVAL_MS) * EXCEPTION_INTERVAL_MS);
};

/**
 * Drop windows that have already ended and clamp a same-day start to now.
 *
 * @param {Array<{ start: Date, end: Date, seats: number }>} items
 * @param {Date} [now]
 * @returns {Array<{ start: Date, end: Date, seats: number }>}
 */
export const clampChangeOneDayExceptions = (items = [], now = new Date()) =>
  (items || [])
    .map(item => {
      const start = asDate(item?.start);
      const end = asDate(item?.end);
      if (!start || !end || end <= start) {
        return null;
      }
      if (end <= now) {
        return null;
      }
      if (start > now) {
        return { ...item, start, end };
      }
      const nextStart = alignExceptionStart(start, now);
      if (nextStart >= end) {
        return null;
      }
      return { ...item, start: nextStart, end };
    })
    .filter(Boolean);

/**
 * User-facing copy when Sharetribe rejects an exception create (past start,
 * overlap, raw axios 400).
 *
 * @param {unknown} error
 * @returns {string}
 */
export const availabilityExceptionSaveMessage = error => {
  if (!error) {
    return null;
  }
  const raw = [error?.error, error?.message, error?.statusText]
    .filter(Boolean)
    .join(' ');
  if (/already passed|later time today/i.test(raw)) {
    return raw;
  }
  if (
    error?.code === 'hours_in_past' ||
    /hours_in_past|in the past|start.*past/i.test(raw)
  ) {
    return 'That time window has already passed. Pick a later time today, or another day.';
  }
  if (/status code 400|overlap|conflict|invalid/i.test(raw) || error?.status === 400) {
    return "We couldn't save those hours. If this is today, remaining time still works — try again, or pick a later window.";
  }
  return (
    (typeof error?.error === 'string' && error.error) ||
    (typeof error?.message === 'string' && !/status code/i.test(error.message)
      ? error.message
      : "We couldn't save those hours. Please try again.")
  );
};

/**
 * Non-overlapping availability exceptions for Off / Different hours.
 * Does not stack a full-day seats=0 on top of a seats=1 window.
 *
 * @param {Object} params
 * @param {string} params.startYmd
 * @param {string} [params.endYmd]
 * @param {'off'|'hours'} params.mode
 * @param {string} [params.from]
 * @param {string} [params.to]
 * @param {string} [params.timeZone]
 * @param {Array} [params.entries]
 * @param {Date} [params.now]
 * @returns {Array<{ start: Date, end: Date, seats: number }>}
 */
export const buildChangeOneDayExceptions = ({
  startYmd,
  endYmd,
  mode,
  from = '09:00',
  to = '17:00',
  timeZone = 'Etc/UTC',
  entries = [],
  now = new Date(),
}) => {
  if (!startYmd) {
    return [];
  }
  const last = endYmd && endYmd >= startYmd ? endYmd : startYmd;
  if (mode === 'off') {
    return clampChangeOneDayExceptions(
      [
        {
          start: zonedDateFromParts(startYmd, '00:00', timeZone),
          end: zonedDateFromParts(addYmdDays(last, 1), '00:00', timeZone),
          seats: 0,
        },
      ],
      now
    );
  }

  const newStart = minutesFromHhmm(from);
  const newEnd = minutesFromHhmm(to);
  if (!Number.isFinite(newStart) || !Number.isFinite(newEnd) || newEnd <= newStart) {
    return [];
  }

  const built = eachYmd(startYmd, last).flatMap(ymd => {
    const usual = usualForYmd(ymd, entries, timeZone);
    if (!usual) {
      return [exception(ymd, from, to, timeZone, 1)];
    }
    const uStart = minutesFromHhmm(usual.startTime);
    const uEnd = minutesFromHhmm(usual.endTime);
    if (newEnd <= uStart || newStart >= uEnd) {
      return [
        exception(ymd, usual.startTime, usual.endTime, timeZone, 0),
        exception(ymd, from, to, timeZone, 1),
      ];
    }
    const out = [];
    if (uStart < newStart) {
      out.push(exception(ymd, usual.startTime, from, timeZone, 0));
    }
    if (newEnd < uEnd) {
      out.push(exception(ymd, to, usual.endTime, timeZone, 0));
    }
    if (newStart < uStart) {
      out.push(exception(ymd, from, usual.startTime, timeZone, 1));
    }
    if (newEnd > uEnd) {
      out.push(exception(ymd, usual.endTime, to, timeZone, 1));
    }
    return out;
  });
  return clampChangeOneDayExceptions(built, now);
};

/**
 * Sharetribe exception id from an SDK entity or a plain payload.
 *
 * @param {Object} item
 * @returns {string|null}
 */
export const exceptionEntityId = item =>
  item?.id?.uuid || (typeof item?.id === 'string' ? item.id : null);

/**
 * Exception seats (0 = not bookable).
 *
 * @param {Object} item
 * @returns {number|null}
 */
export const exceptionSeats = item => {
  const seats = item?.attributes?.seats ?? item?.seats;
  return seats === 0 || seats === 1 ? seats : null;
};

/**
 * Exception start instant.
 *
 * @param {Object} item
 * @returns {Date|null}
 */
export const exceptionStart = item => asDate(item?.attributes?.start ?? item?.start);

/**
 * Exception end instant.
 *
 * @param {Object} item
 * @returns {Date|null}
 */
export const exceptionEnd = item => asDate(item?.attributes?.end ?? item?.end);

const isMidnightInZone = (date, timeZone) => {
  const zoned = moment(date).tz(timeZone || 'Etc/UTC');
  return zoned.hours() === 0 && zoned.minutes() === 0;
};

/**
 * One-off full-day time-off rows. These are availabilityExceptions with
 * seats=0 from midnight to midnight — never weekly plan entries.
 *
 * @param {Array} items
 * @param {string} [timeZone]
 * @returns {Array<{ id: string|null, start: Date, end: Date, seats: 0, startYmd: string, lastYmd: string }>}
 */
export const oneOffDayOffsFromExceptions = (items = [], timeZone = 'Etc/UTC') => {
  return (items || [])
    .map(item => {
      const start = exceptionStart(item);
      const end = exceptionEnd(item);
      const seats = exceptionSeats(item);
      if (!start || !end || seats !== 0) {
        return null;
      }
      if (!isMidnightInZone(start, timeZone) || !isMidnightInZone(end, timeZone)) {
        return null;
      }
      const startYmd = ymdInTimeZone(start, timeZone);
      const endYmd = ymdInTimeZone(end, timeZone);
      const lastYmd = addYmdDays(endYmd, -1);
      if (!startYmd || !lastYmd || lastYmd < startYmd) {
        return null;
      }
      return {
        id: exceptionEntityId(item),
        start,
        end,
        seats: 0,
        startYmd,
        lastYmd,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.startYmd < b.startYmd ? -1 : a.startYmd > b.startYmd ? 1 : 0));
};

/**
 * Format a YYYY-MM-DD as "Friday, Oct 3".
 *
 * @param {string} ymd
 * @param {{ formatDate: Function }} intl
 * @returns {string}
 */
export const formatDayOffDate = (ymd, intl) => {
  if (!ymd || typeof intl?.formatDate !== 'function') {
    return ymd || '';
  }
  const [year, month, day] = ymd.split('-').map(part => Number.parseInt(part, 10));
  if (!year || !month || !day) {
    return ymd;
  }
  return intl.formatDate(new Date(year, month - 1, day), {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Single date or inclusive range label.
 *
 * @param {string} startYmd
 * @param {string} [lastYmd]
 * @param {{ formatDate: Function }} intl
 * @returns {string}
 */
export const formatDayOffWhen = (startYmd, lastYmd, intl) => {
  const startLabel = formatDayOffDate(startYmd, intl);
  if (!lastYmd || lastYmd === startYmd) {
    return startLabel;
  }
  return `${startLabel} – ${formatDayOffDate(lastYmd, intl)}`;
};

/**
 * Toast date from a just-saved day-off exception payload.
 *
 * @param {Array} exceptions
 * @param {string} [timeZone]
 * @param {{ formatDate: Function }} intl
 * @returns {{ when: string, isRange: boolean }}
 */
export const dayOffWhenFromExceptions = (exceptions = [], timeZone = 'Etc/UTC', intl) => {
  const rows = oneOffDayOffsFromExceptions(exceptions, timeZone);
  if (rows.length) {
    const first = rows[0];
    const last = rows[rows.length - 1];
    return {
      when: formatDayOffWhen(first.startYmd, last.lastYmd, intl),
      isRange: first.startYmd !== last.lastYmd,
    };
  }
  const start = exceptionStart(exceptions[0]);
  if (!start) {
    return { when: '', isRange: false };
  }
  return {
    when: formatDayOffDate(ymdInTimeZone(start, timeZone), intl),
    isRange: false,
  };
};
